import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type CellValueChangedEvent,
  type ColDef,
  type ColumnPinnedEvent,
  type ColumnResizedEvent,
  type ColumnState,
  type ColumnVisibleEvent,
  type FirstDataRenderedEvent,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type RowClassParams,
  type RowSelectionOptions,
  type SelectionChangedEvent,
  type SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";

import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { EmptyCard, type EmptyCardProps } from "../Cards/EmptyCard/EmptyCard";

import { type DataGridAnyColDef, type DataGridColDef } from "./types";
import { getColumnId, isColumnGroup } from "./utils";

type DataGridProps<TData> = Omit<AgGridReactProps<TData>, "rowSelection"> & {
  rowSelectionOptions?:
    | Partial<RowSelectionOptions<TData>>
    | "multiple"
    | "single";
  selectedRows?: Record<string, boolean>;
  onSelectedRowsChange?: (selection: Record<string, boolean>) => void;
  emptyState?: EmptyCardProps;
  /**
   * Shows a totals row summing every column flagged `aggregateTotal`. Pinned to
   * the bottom by default; pass `pinnedTotalsToTop` to pin it to the top.
   */
  showTotals?: boolean;
  /**
   * Pins the `showTotals` row to the top instead of the bottom.
   */
  pinnedTotalsToTop?: boolean;
  /**
   * Plug-in persistence for column order / sort / width. The grid applies
   * `value` on the first grid-ready cycle and on every subsequent change
   * to it; every column-state change (sort, resize finished, move
   * finished) fires `onChange` with the latest snapshot. Pairs with
   * `useColumnToggle`'s `storage` prop which owns visibility + pinning.
   */
  columnStateStorage?: {
    value?: ColumnState[];
    onChange?: (next: ColumnState[]) => void;
  };
  /**
   * Hides the column header row. Use during a cold load so the loading overlay
   * isn't topped by column titles sitting over an empty grid. Purely visual —
   * column state is untouched, so the headers reappear when toggled back off.
   */
  hideColumnHeaders?: boolean;
};

const DEFAULT_ROW_SELECTION = {
  mode: "multiRow",
  enableClickSelection: false,
  hideDisabledCheckboxes: true,
} as const;

// Internal marker stamped on the generated totals row so styling and cell
// routing can target it without colliding with consumer-pinned rows.
const TOTAL_ROW_FLAG = "__dataGridTotalRow";

// Row class applied to totals rows (DataGrid-generated or consumer-pinned) so
// `DataGrid.css` can style them. Exported for consumers with their own
// `getRowClass` (e.g. a BE-provided pinned totals row).
export const DATAGRID_TOTAL_ROW_CLASS = "datagrid-total-row";

/**
 * True for the totals row DataGrid generates (see `showTotals`). Use in
 * `editable`/`isRowSelectable` guards.
 */
export const isDataGridTotalRow = (data: unknown): boolean =>
  !!(data as Record<string, unknown> | undefined)?.[TOTAL_ROW_FLAG];

const getLeaves = <TData,>(
  columns: DataGridColDef<TData>[],
): DataGridColDef<TData>[] =>
  columns.flatMap((col) =>
    isColumnGroup(col) ? getLeaves(col.children) : [col],
  );

// On the totals row, route each cell to its `totalCellRenderer` (or blank when
// absent) and force it non-editable; normal rows keep their own renderer and
// `editable`.
const withTotalRowColumnOverrides = <TData,>(
  columns: DataGridAnyColDef<TData>[],
): DataGridAnyColDef<TData>[] =>
  columns.map((col) => {
    if (isColumnGroup(col)) {
      return { ...col, children: withTotalRowColumnOverrides(col.children) };
    }

    // `aggregateTotal` is omitted via the rest sibling so it never reaches
    // AG Grid (which would warn on the unknown colDef property).
    const {
      totalCellRenderer,
      aggregateTotal: _,
      ...colDef
    } = col as DataGridColDef<TData>;

    return {
      ...colDef,
      cellRendererSelector: (params) => {
        if (isDataGridTotalRow(params.data)) {
          return { component: totalCellRenderer ?? (() => null) };
        }

        return typeof colDef.cellRendererSelector === "function"
          ? colDef.cellRendererSelector(params)
          : undefined;
      },
      editable: (params) => {
        if (isDataGridTotalRow(params.data)) return false;

        return typeof colDef.editable === "function"
          ? colDef.editable(params)
          : (colDef.editable ?? false);
      },
    };
  });

const SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn";

// applyOrder pushes columns missing from the stored state to the end, so a
// layout saved before row selection existed would order the checkbox column
// after the pinned data columns. The selection column always leads.
const withSelectionColumnFirst = (state: ColumnState[]): ColumnState[] => [
  { colId: SELECTION_COLUMN_ID, pinned: "left" },
  ...state.filter((column) => column.colId !== SELECTION_COLUMN_ID),
];

const toColumnStatePinned = (
  pinned: ColDef["pinned"],
): ColumnState["pinned"] => {
  if (pinned === "right") return "right";

  return pinned ? "left" : null;
};

export const DataGrid = <TData,>({
  rowSelectionOptions,
  onSelectedRowsChange,
  selectedRows,
  emptyState,
  selectionColumnDef,
  showTotals,
  pinnedTotalsToTop,
  columnDefs,
  pinnedTopRowData,
  pinnedBottomRowData,
  columnStateStorage,
  hideColumnHeaders,
  className,
  onCellValueChanged: onCellValueChangedFromProps,
  onFirstDataRendered: onFirstDataRenderedFromProps,
  getRowId: getRowIdFromProps,
  getRowClass: getRowClassFromProps,
  onColumnVisible: onColumnVisibleFromProps,
  onColumnPinned: onColumnPinnedFromProps,
  onGridReady: onGridReadyFromProps,
  onColumnResized: onColumnResizedFromProps,
  onSortChanged: onSortChangedFromProps,
  ...props
}: DataGridProps<TData>) => {
  const gridRef = useRef<AgGridReact<TData>>(null);
  const [totalRow, setTotalRow] = useState<TData[]>([]);

  const gridApi = gridRef.current?.api;

  const hasSelection =
    rowSelectionOptions != null ||
    selectedRows != null ||
    onSelectedRowsChange != null;
  const mergedRowSelection = hasSelection
    ? {
        ...DEFAULT_ROW_SELECTION,
        ...(typeof rowSelectionOptions === "object" ? rowSelectionOptions : {}),
      }
    : undefined;

  // #region Row selection

  const handleSelectionChanged = (event: SelectionChangedEvent<TData>) => {
    if (!onSelectedRowsChange) return;
    const next: Record<string, boolean> = {};
    event.api.getSelectedNodes().forEach((node) => {
      if (node.id) next[node.id] = true;
    });
    onSelectedRowsChange(next);
  };

  const handleGetRowClass = (params: RowClassParams<TData>) => {
    const consumerClass =
      typeof getRowClassFromProps === "function"
        ? getRowClassFromProps(params)
        : getRowClassFromProps;
    const classes = [consumerClass ?? []].flat();

    if (isDataGridTotalRow(params.data)) classes.push(DATAGRID_TOTAL_ROW_CLASS);

    return classes;
  };

  useEffect(() => {
    if (!gridApi || !selectedRows) return;
    gridApi.forEachNode((node) => {
      const shouldBeSelected = !!selectedRows[node.id ?? ""];

      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected, false, "api");
      }
    });
  }, [gridApi, selectedRows, props.rowData]);

  // #endregion

  // #region Totals row

  const { usesTotals, accumulateColIds } = useMemo(() => {
    const leaves = getLeaves(columnDefs ?? []);

    return {
      usesTotals: leaves.some((leaf) => leaf.totalCellRenderer),
      accumulateColIds: leaves
        .filter((leaf) => leaf.aggregateTotal)
        .map(getColumnId)
        .filter(Boolean),
    };
  }, [columnDefs]);

  const routedColumnDefs = useMemo(
    () =>
      usesTotals && columnDefs
        ? withTotalRowColumnOverrides(columnDefs)
        : columnDefs,
    [usesTotals, columnDefs],
  );

  const updateTotalRow = useCallback(() => {
    const getTotalRow = () => {
      if (!gridApi || !showTotals || accumulateColIds.length === 0) return [];

      const sums: Record<string, number> = {};
      let rowCount = 0;
      gridApi.forEachNode((node) => {
        rowCount += 1;
        accumulateColIds.forEach((colId) => {
          const value = gridApi.getCellValue({ rowNode: node, colKey: colId });
          sums[colId] =
            (sums[colId] ?? 0) + (typeof value === "number" ? value : 0);
        });
      });

      if (rowCount === 0) return [] as TData[];

      return [
        {
          ...sums,
          [TOTAL_ROW_FLAG]: true,
        },
      ] as TData[];
    };
    setTotalRow(getTotalRow());
  }, [gridApi, showTotals, accumulateColIds]);

  useEffect(() => {
    updateTotalRow();
  }, [updateTotalRow, props.rowData]);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.refreshCells({
      force: true,
    });
  }, [gridApi, totalRow]);

  const handleFirstDataRendered = (event: FirstDataRenderedEvent<TData>) => {
    onFirstDataRenderedFromProps?.(event);
    updateTotalRow();
  };
  const handleCellValueChanged = (event: CellValueChangedEvent<TData>) => {
    onCellValueChangedFromProps?.(event);
    updateTotalRow();
  };

  /**
   * Prevent undefined rowId in total row
   */
  const getRowId = useMemo(() => {
    if (!getRowIdFromProps) return undefined;

    return (params: GetRowIdParams<TData>) => {
      if (isDataGridTotalRow(params.data)) {
        return TOTAL_ROW_FLAG;
      }

      return getRowIdFromProps(params);
    };
  }, [getRowIdFromProps]);

  // #endregion

  // Toggling a column's visibility/pin (e.g. via useColumnToggle) pushes a new
  // columnDefs array into ag-grid, which can reprocess column order and leave
  // the auto-generated selection checkbox column out of first place in the
  // pinned-left group. Re-assert it after every event that could reorder.
  const enforceSelectionColumnFirst = (api: GridApi<TData>) => {
    if (!hasSelection) return;

    const currentState = api.getColumnState();

    if (currentState[0]?.colId === SELECTION_COLUMN_ID) return;

    api.applyColumnState({
      state: withSelectionColumnFirst(currentState),
      applyOrder: true,
    });
  };

  // Hiding a column (e.g. via useColumnToggle) leaves ag-grid's sort on it
  // without firing sortChanged — an invisible sort with no indicator or way to
  // clear it. Clearing it here re-fires sortChanged so consumers stay in sync.
  const handleColumnVisible = (event: ColumnVisibleEvent<TData>) => {
    const hiddenSortedColumns = event.api
      .getColumnState()
      .filter((column) => column.sort && column.hide);

    if (hiddenSortedColumns.length > 0) {
      event.api.applyColumnState({
        state: hiddenSortedColumns.map(({ colId }) => ({ colId, sort: null })),
      });
    }

    enforceSelectionColumnFirst(event.api);
    onColumnVisibleFromProps?.(event);
  };

  const handleColumnPinned = (event: ColumnPinnedEvent<TData>) => {
    enforceSelectionColumnFirst(event.api);
    onColumnPinnedFromProps?.(event);
  };

  const emitColumnState = useCallback(() => {
    if (!gridApi || !columnStateStorage?.onChange) return;
    columnStateStorage.onChange(gridApi.getColumnState());
  }, [gridApi, columnStateStorage]);

  const toStorableColumnState = (state: ColumnState[]) =>
    hasSelection ? withSelectionColumnFirst(state) : state;

  // Priming the storage here (not at firstDataRendered) means a consumer that
  // reads the working state before any rows render — e.g. saving a table
  // layout while the query is still loading — still captures the real column
  // state instead of an empty snapshot.
  const handleGridReady = (event: GridReadyEvent<TData>) => {
    if (columnStateStorage?.value) {
      event.api.applyColumnState({
        state: toStorableColumnState(columnStateStorage.value),
        applyOrder: true,
      });
    }

    columnStateStorage?.onChange?.(event.api.getColumnState());
    onGridReadyFromProps?.(event);
  };

  // Re-apply when the backing store delivers a new value (async BE load, or
  // switching saved layouts). useLayoutEffect runs before paint, so the
  // columns update without flashing the previous layout for a frame. When the
  // value clears after having been set (e.g. selecting the "Default" layout),
  // reset ag-grid to its built-in column state.
  const previousColumnStateValueRef = useRef(columnStateStorage?.value);
  useLayoutEffect(() => {
    if (!gridApi) return;

    if (columnStateStorage?.value) {
      gridApi.applyColumnState({
        state: toStorableColumnState(columnStateStorage.value),
        applyOrder: true,
      });
    } else if (previousColumnStateValueRef.current) {
      gridApi.resetColumnState();
      // Report the reset so a consumer tracking the working state for a
      // later save doesn't keep the pre-reset snapshot.
      columnStateStorage?.onChange?.(gridApi.getColumnState());
    }

    previousColumnStateValueRef.current = columnStateStorage?.value;
  }, [gridApi, columnStateStorage?.value]);

  const handleColumnResized = (event: ColumnResizedEvent<TData>) => {
    if (event.finished && event.source === "uiColumnResized") {
      emitColumnState();
    }

    onColumnResizedFromProps?.(event);
  };

  const handleSortChanged = (event: SortChangedEvent<TData>) => {
    if (event.source === "uiColumnSorted") emitColumnState();

    onSortChangedFromProps?.(event);
  };

  const pinnedColumnsKey = getLeaves(routedColumnDefs ?? [])
    .map((leaf) => `${getColumnId(leaf)}:${String(leaf.pinned)}`)
    .join("|");

  useEffect(() => {
    if (!gridApi) return;

    const state = getLeaves(routedColumnDefs ?? [])
      .map((leaf) => ({
        colId: getColumnId(leaf),
        pinned: toColumnStatePinned(leaf.pinned),
      }))
      .filter((entry) => Boolean(entry.colId));

    if (state.length === 0) return;

    gridApi.applyColumnState({ state });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, pinnedColumnsKey]);

  // Stable component reading params keeps the overlay reactive — ag-grid
  // re-renders custom overlays on params changes, not on component identity
  const EmptyStateComponent = useCallback(
    ({ emptyState }: { emptyState?: EmptyCardProps }) => {
      const { className, title, ...rest } = emptyState ?? {};

      return (
        <EmptyCard
          className={cnTwMerge("p-0 mb-8", className)}
          noBorder
          title={title ?? m.common_noResultsFound()}
          {...rest}
        />
      );
    },
    [],
  );

  return (
    <AgGridReact
      className={cnTwMerge(
        className,
        hideColumnHeaders && "datagrid-hide-header",
      )}
      columnDefs={routedColumnDefs}
      domLayout="autoHeight"
      getRowClass={handleGetRowClass}
      getRowId={getRowId}
      noRowsOverlayComponent={EmptyStateComponent}
      noRowsOverlayComponentParams={{ emptyState }}
      onCellValueChanged={handleCellValueChanged}
      onColumnPinned={handleColumnPinned}
      onColumnResized={handleColumnResized}
      onColumnVisible={handleColumnVisible}
      onFirstDataRendered={handleFirstDataRendered}
      onGridReady={handleGridReady}
      onSelectionChanged={handleSelectionChanged}
      onSortChanged={handleSortChanged}
      pinnedBottomRowData={[
        pinnedBottomRowData ?? [],
        pinnedTotalsToTop ? [] : totalRow,
      ].flat()}
      pinnedTopRowData={[
        pinnedTotalsToTop ? totalRow : [],
        pinnedTopRowData ?? [],
      ].flat()}
      ref={gridRef}
      rowSelection={mergedRowSelection}
      selectionColumnDef={{ pinned: "left", ...selectionColumnDef }}
      singleClickEdit
      stopEditingWhenCellsLoseFocus
      {...props}
    />
  );
};
